import { Model } from '../../../../types/model'
import { OverlayComponentProps } from '../../types'
import { AlertInfo, PrimaryAction, Text } from '@ohgoodlord/design-system'
import { DefaultHeader } from '../default-header'
import { DefaultFooter } from '../default-footer'
import { css } from '@emotion/react'
import { FormProvider, useForm } from 'react-hook-form'
import { Country } from '../../../../../common/countryData'
import { MobileMeta } from '../../../../../common/mobileData'
import { InputTextFormField } from './components/InputTextFormField'
import { findNationality, getInitialDob, getInitialMobileValues } from './utils'
import { NationalityDropdown } from './components/NationalityDropdown'
import { getValidationRules } from './validation'
import { SubjectDetailsDobField } from './components/SubjectDetailsDobField'
import { PhoneNumberFormField } from './components/PhoneNumberFormField'
import PhoneNumber from 'awesome-phonenumber'
import { useEffect, useMemo } from 'react'
import { logErrorToDatadog } from '../../../../../common/utils'
import { useStore } from '../../../../hooks/use-store'

export const SUBJECT_DETAILS = 'subject-details' as const

export interface SubjectDetailsOverlayModel extends Model<typeof SUBJECT_DETAILS> {
  overlayTitle: string
  message?: string
  dob?: string
  isMobileValid: boolean
  isDOBValid: boolean
  firstName?: string
  lastName?: string
  middleName?: string
  countries: Country[]
  mobile: string
  nationality?: string | null
  mobileMeta: MobileMeta
  options?: {
    isScottishResident?: boolean
    disableEditActionOnSubmit?: boolean
  }
}

export interface FormValues {
  firstName: string
  middleName?: string
  lastName: string
  dob: string
  nationality: Country | null
  mobile: string
  mobileMeta: MobileMeta
}

export type Props = OverlayComponentProps<SubjectDetailsOverlayModel>

export const SubjectDetailsOverlay = ({
  model,
  isMobile,
  renderOverlayHeader,
  onOverlaySubmit,
  onSendEvent,
  onSendMessage,
  onClose,
}: Props) => {
  const { disableEditActions } = useStore()

  const initialNationality = useMemo(
    () => findNationality(model.nationality),
    [model.nationality],
  )

  useEffect(() => {
    if (model.nationality && initialNationality === null) {
      logErrorToDatadog(
        `Unable to map nationality code ${model.nationality} to a country`,
        { nationalityCode: model.nationality },
      )
    }
  }, [])

  const methods = useForm<FormValues>({
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: model.firstName,
      middleName: model.middleName,
      lastName: model.lastName,
      dob: getInitialDob(model.dob),
      nationality: initialNationality,
      ...getInitialMobileValues(model.mobile),
    },
  })

  const { dirtyFields } = methods.formState

  const isScottishResident = model.options?.isScottishResident ?? false

  const validationRules = getValidationRules({ isScottishResident })

  const onSubmit = ({
    firstName,
    middleName,
    lastName,
    dob,
    nationality,
    mobile,
    mobileMeta,
  }: FormValues) => {
    if (model.options?.disableEditActionOnSubmit === true) {
      disableEditActions()
    }

    const phoneNumber = new PhoneNumber(mobile, mobileMeta.iso2)
    const isMobileValid = phoneNumber.isValid()
    const mobileToSend = dirtyFields.mobile
      ? phoneNumber.getNumber('national')
      : model.mobile

    const nameForMessage = `${firstName} ${middleName ?? ''} ${lastName}`.replace(
      /\s+/g,
      ' ',
    )
    const dobForMessage = dob.split('T')[0]

    onSendMessage({
      message: `Confirmed ${nameForMessage} born on ${dobForMessage} with nationality ${nationality?.nationality} and contact number ${mobileToSend}`,
    })
    onSendEvent({
      name: 'form',
      value: {
        dob,
        isMobileValid,
        isDOBValid: true,
        firstName,
        lastName,
        middleName,
        mobile: mobileToSend,
        mobileMeta,
        nationality,
        isScottishResident,
      },
    })
    onOverlaySubmit()
  }

  return (
    <form
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
      `}
      onSubmit={methods.handleSubmit(onSubmit)}
      noValidate
    >
      <div>
        {renderOverlayHeader !== undefined ? (
          renderOverlayHeader()
        ) : (
          <DefaultHeader
            modal={model.modal}
            isMobile={isMobile}
            title={model.overlayTitle}
            onClose={onClose}
          />
        )}
        <FormProvider {...methods}>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 15px;
              padding: 15px;
            `}
          >
            <AlertInfo title="Use your full legal name">
              <Text>
                Enter your name exactly as it appears on your passport or driving licence.
              </Text>
            </AlertInfo>
            <InputTextFormField
              label="First name(s)"
              fieldName="firstName"
              rules={validationRules.firstName}
            />
            <InputTextFormField label="Middle name(s)" fieldName="middleName" />
            <InputTextFormField
              label="Last name(s)"
              fieldName="lastName"
              rules={validationRules.lastName}
            />
            <SubjectDetailsDobField
              rules={validationRules.dob}
              isScottishResident={isScottishResident}
            />
            <PhoneNumberFormField rules={validationRules.mobile} />
            <NationalityDropdown rules={validationRules.nationality} />
          </div>
        </FormProvider>
      </div>
      <DefaultFooter
        renderActions={() => (
          <PrimaryAction type="submit" isFullWidth testId="modal-primary-action-button">
            Done
          </PrimaryAction>
        )}
      />
    </form>
  )
}
